import { Button, Chip, Input } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type ApiKeyCreatedResponse,
  type ApiKeyScope,
  useCreateApiKeyMutation,
  useListApiKeysQuery,
  useRevokeApiKeyMutation,
} from '@/features/api-keys/apiKeysApi';
import { t } from '@/lib/i18n';

const ALL_SCOPES: Array<{ value: ApiKeyScope; label: string; description: string }> = [
  { value: 'send:write', label: 'send:write', description: 'POST /api/send/:id — send emails via this key' },
  { value: 'send:read', label: 'send:read', description: 'GET /api/sends — read delivery log' },
  { value: 'templates:read', label: 'templates:read', description: 'GET /api/templates — list & read templates' },
  {
    value: 'templates:write',
    label: 'templates:write',
    description: 'PUT /api/templates/:id/versions — edit templates via API',
  },
  { value: 'webhooks:read', label: 'webhooks:read', description: 'GET /api/webhooks — read webhook delivery log' },
];

export function ApiKeysScreen() {
  const { data: keys, isLoading } = useListApiKeysQuery();
  const [createApiKey, { isLoading: isCreating }] = useCreateApiKeyMutation();
  const [revokeApiKey] = useRevokeApiKeyMutation();
  const [showForm, setShowForm] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState<ApiKeyCreatedResponse | null>(null);

  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>(['send:write']);
  const [environment, setEnvironment] = useState<'live' | 'test'>('live');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');

  const submit = async () => {
    if (!name || selectedScopes.length === 0) return;
    const res = await createApiKey({
      name,
      scopes: selectedScopes,
      environment,
      expiresInDays: expiresInDays === '' ? undefined : Number(expiresInDays),
    }).unwrap();
    setNewlyCreated(res);
    setShowForm(false);
    setName('');
    setSelectedScopes(['send:write']);
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.api_keys')]}
        right={
          !showForm ? (
            <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
              <Icon.plus size={13} /> New key
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          )
        }
      />
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          {newlyCreated && (
            <div
              className="mf-card"
              style={{
                padding: 20,
                marginBottom: 20,
                background: 'linear-gradient(135deg, var(--flame-soft), var(--paper))',
                border: '1px solid var(--flame-line)',
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'var(--flame-2)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                  marginBottom: 8,
                }}
              >
                New key · copy now — this is the only time you'll see it
              </div>
              <h3 className="display" style={{ fontSize: 20, margin: '0 0 12px' }}>
                {newlyCreated.name}
              </h3>
              <div
                className="mono"
                style={{
                  padding: 12,
                  background: 'var(--ink)',
                  color: 'var(--paper)',
                  borderRadius: 'var(--r-2)',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span style={{ flex: 1 }}>{newlyCreated.token}</span>
                <Button size="sm" style={{ background: 'var(--paper-3)', color: 'var(--ink)' }}
                  onClick={() => navigator.clipboard.writeText(newlyCreated.token)}
                >
                  <Icon.copy size={12} /> Copy
                </Button>
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setNewlyCreated(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}

          {showForm ? (
            <div className="mf-card" style={{ padding: 28, maxWidth: 640 }}>
              <h2 className="display" style={{ fontSize: 22, margin: '0 0 6px' }}>
                New API key
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>
                Stored as SHA-256 hash — the raw token appears once, so grab it now.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field label="Name">
                  <Input
                    
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Production backend" />
                </Field>

                <Field label="Environment">
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(['live', 'test'] as const).map((env) => (
                      <label
                        key={env}
                        style={{
                          flex: 1,
                          padding: 10,
                          border: `1px solid ${environment === env ? 'var(--flame)' : 'var(--line)'}`,
                          borderRadius: 'var(--r-2)',
                          cursor: 'pointer',
                          background: environment === env ? 'var(--flame-soft)' : 'var(--paper)',
                          textAlign: 'center',
                        }}
                      >
                        <input
                          type="radio"
                          name="env"
                          checked={environment === env}
                          onChange={() => setEnvironment(env)}
                          style={{ display: 'none' }}
                        />
                        <div style={{ fontSize: 13, fontWeight: 550 }}>{env === 'live' ? 'Live' : 'Test'}</div>
                        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2 }}>
                          emk_{env}_…
                        </div>
                      </label>
                    ))}
                  </div>
                </Field>

                <Field label="Scopes">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ALL_SCOPES.map((s) => (
                      <label
                        key={s.value}
                        style={{
                          display: 'flex',
                          gap: 10,
                          padding: 10,
                          border: `1px solid ${selectedScopes.includes(s.value) ? 'var(--flame)' : 'var(--line)'}`,
                          borderRadius: 'var(--r-2)',
                          cursor: 'pointer',
                          background: selectedScopes.includes(s.value) ? 'var(--flame-soft)' : 'var(--paper)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(s.value)}
                          onChange={(e) =>
                            setSelectedScopes(
                              e.target.checked
                                ? [...selectedScopes, s.value]
                                : selectedScopes.filter((x) => x !== s.value),
                            )
                          }
                        />
                        <div>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 550 }}>
                            {s.label}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{s.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </Field>

                <Field label="Expires in (days) · optional">
                  <Input
                    
                    type="number"
                    placeholder="Never"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value === '' ? '' : Number(e.target.value))} />
                </Field>

                <Button variant="flame" onClick={submit}
                  disabled={!name || selectedScopes.length === 0 || isCreating}>
                  {isCreating ? 'Creating…' : 'Create key'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="display" style={{ fontSize: 32, margin: '0 0 8px' }}>
                API keys
              </h1>
              <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 20px' }}>
                Use keys to call <code className="mono">POST /api/send/:templateId</code> from your backend. Keys are
                scoped and rate-limited.
              </p>

              {isLoading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Loading…
                </div>
              )}

              {!isLoading && (keys ?? []).length === 0 && (
                <div className="mf-card" style={{ padding: 40, textAlign: 'center' }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                      color: 'var(--ink-3)',
                    }}
                  >
                    No API keys yet
                  </div>
                  <h2 className="display" style={{ fontSize: 22, margin: '10px 0 8px' }}>
                    Create your first key
                  </h2>
                  <Button variant="flame" onClick={() => setShowForm(true)}>
                    <Icon.plus size={13} /> New key
                  </Button>
                </div>
              )}

              {(keys ?? []).length > 0 && (
                <div className="mf-card" style={{ overflow: 'hidden' }}>
                  <table className="mf-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Prefix</th>
                        <th>Scopes</th>
                        <th>Env</th>
                        <th>Rate limit</th>
                        <th>Last used</th>
                        <th>Expires</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {keys?.map((k) => (
                        <tr key={k.id}>
                          <td style={{ fontSize: 13, fontWeight: 500 }}>{k.name}</td>
                          <td className="mono" style={{ fontSize: 11.5 }}>
                            {k.prefix}…
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {k.scopes.map((s) => (
                                <Chip dot={false} key={s}>
                                  {s}
                                </Chip>
                              ))}
                            </div>
                          </td>
                          <td>
                            <Chip variant={k.environment === 'live' ? 'flame' : 'draft'}>{k.environment}</Chip>
                          </td>
                          <td className="mono" style={{ fontSize: 11.5 }}>
                            {k.rateLimitPerMin}/min
                          </td>
                          <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                            {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}
                          </td>
                          <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                            {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td>
                            <Button variant="ghost" size="sm" icon onClick={async () => {
                                if (window.confirm(`Revoke ${k.name}?`)) {
                                  await revokeApiKey(k.id).unwrap();
                                }
                              }}
                            >
                              <Icon.trash size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
